import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Drawer,
  InputLabel,
  FormControl,
  Stack,
  InputAdornment,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { Button } from '@liminal/web-ui/primitives/Button';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { Input } from '@liminal/web-ui/forms/Input';
import { styled } from '@mui/material/styles';
import { useQueries, useQuery } from '@tanstack/react-query';
import ForceGraph2D from 'react-force-graph-2d';
import type { ForceGraphMethods } from 'react-force-graph-2d';
import { nimGraphKeys, useNimMemoryGraph, useNimGraphStats } from '@/hooks/useNimGraph';
import { useDebounce } from '@/hooks/useDebounce';
import { adminClient } from '@/lib/adminClient';
import type { GraphNode, GraphEdge, Profile } from '@/api/types';
import { GraphNodeKind } from '@/api/generated';
import { brandColors } from '@liminal/brand/palette';
import { fontSizes, fontWeights } from '@liminal/brand/scale';
import { FONT_STACK_BODY } from '@liminal/brand/font-faces';
import { LoadingSpinner } from '@liminal/web-ui/feedback/LoadingSpinner';
import { Icon } from '@liminal/web-ui/icons/Icon';
import { SegmentedToggle, type SegmentedOption } from '@liminal/web-ui/forms/SegmentedToggle';
import { getErrorMessage } from '@/errors/messages';

const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Lightweight profile shape used to enrich UUID-shaped owner / entity identifiers.
interface UserProfileMini {
  userId: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
}

function toMiniProfile(p: Profile, fallbackUid: string): UserProfileMini {
  const firstName = (p.firstName ?? '').trim();
  const lastName = (p.lastName ?? '').trim();
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  return {
    userId: p.userId ?? fallbackUid,
    email: p.email ?? '',
    displayName: name || p.email || fallbackUid,
    firstName,
    lastName,
  };
}

// Initials for the avatar drawn inside user-entity nodes. Prefers first+last
// (e.g. "JM" for Jack Marsh); falls back to the first letter of whatever
// identifying text we have.
function userInitials(p: UserProfileMini): string {
  if (p.firstName && p.lastName) return (p.firstName.charAt(0) + p.lastName.charAt(0)).toUpperCase();
  if (p.firstName) return p.firstName.charAt(0).toUpperCase();
  if (p.lastName) return p.lastName.charAt(0).toUpperCase();
  if (p.email) return p.email.charAt(0).toUpperCase();
  return '?';
}

// useUserProfiles batch-fetches profiles for any UUID-shaped identifier so the
// page can render `name (email)` instead of raw UUIDs. A single batched request
// (LIM-1832) replaces a per-id getUser fan-out; unknown ids are omitted.
function useUserProfiles(uids: string[]) {
  const unique = useMemo(
    () => Array.from(new Set(uids.filter((u): u is string => Boolean(u) && isUUID.test(u)))).sort(),
    [uids],
  );
  return useQuery({
    queryKey: ['nim-graph-profiles', unique],
    queryFn: async () => {
      const byId = await adminClient.adminBatchGetUsers(unique);
      const out: Record<string, UserProfileMini> = {};
      for (const uid of unique) {
        const p = byId[uid];
        if (p) out[uid] = toMiniProfile(p, uid);
      }
      return out;
    },
    enabled: unique.length > 0,
    staleTime: 10 * 60 * 1000,
  });
}

type ViewMode = 'memory' | 'entity' | 'full';

interface OwnerOption {
  userId: string;
  displayName: string;
  email: string;
}

interface ForceNode {
  id: string;       // composite key (e.g. "MEMORY:<uuid>" or "ENTITY:<owner>:<name>") — used by d3-force for identity
  rawId: string;    // the underlying entity name or memory UUID — what the server's ExpandNode RPC expects
  label: string;
  kind: GraphNodeKind;
  ownerId: string;
  weight: number;
  // Injected by d3-force after the first simulation tick; we set fx/fy in
  // onNodeDragEnd so dragged nodes stay where the user dropped them.
  x?: number;
  y?: number;
  fx?: number;
  fy?: number;
}

// After the simulation runs d3-force mutates link.source / link.target from
// the original string IDs into the actual node objects. Either may show up,
// hence the `ForceNode | string` union.
type ForceLinkEnd = string | ForceNode;

interface ForceLink {
  source: ForceLinkEnd;
  target: ForceLinkEnd;
  predicate: string;
  weight: number;
}

interface ForceGraphData {
  nodes: ForceNode[];
  links: ForceLink[];
}

const StatsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
  flexWrap: 'wrap',
}));

// Liminal brand palette (theme.ts). Entity nodes use the bright accent orange;
// Memory nodes use the brown so the two kinds are distinguishable at a glance.
function nodeColor(kind: GraphNodeKind): string {
  if (kind === GraphNodeKind.GRAPH_NODE_KIND_MEMORY) return brandColors.brown;
  if (kind === GraphNodeKind.GRAPH_NODE_KIND_ENTITY) return brandColors.orange;
  return brandColors.beige;
}

// react-force-graph-2d derives the rendered radius from `nodeVal` as
// `nodeRelSize * sqrt(val)`. We extract this so the initials overlay can size
// itself to fit inside the same circle.
const NODE_REL_SIZE = 5;
function nodeVal(n: ForceNode): number {
  // Memory weight is importance (0..1) — small linear scale.
  if (n.kind === GraphNodeKind.GRAPH_NODE_KIND_MEMORY) return 1 + (n.weight || 0) * 3;
  // Entity weight is degree (0..N) — log-scale so 100-degree hubs don't dwarf
  // 1-degree leaves into invisibility.
  return 1 + Math.log2(1 + (n.weight || 0)) * 2;
}
function nodeRadius(n: ForceNode): number {
  return NODE_REL_SIZE * Math.sqrt(nodeVal(n));
}

function toForceData(nodes: GraphNode[], edges: GraphEdge[]): ForceGraphData {
  return {
    nodes: nodes.map((n) => ({
      id: n.key,
      rawId: n.id,
      label: n.label || n.id,
      kind: n.kind,
      ownerId: n.ownerId ?? '',
      weight: n.weight ?? 0,
    })),
    links: edges.map((e) => ({
      source: e.sourceKey,
      target: e.targetKey,
      predicate: e.predicate,
      weight: e.weight ?? 0,
    })),
  };
}

// linkEndId extracts a stable key from either side of a link, working both
// before and after d3-force has rewritten `source` / `target` from a string
// into the resolved node object.
function linkEndId(end: ForceLinkEnd): string {
  return typeof end === 'string' ? end : end.id;
}

// useStableArray returns the same array reference across renders as long as
// every element is reference-equal to the previous render's element. Used to
// stop fresh `useQueries` array shells from invalidating downstream useMemos
// (which would otherwise rebuild the graph data and reset the simulation).
function useStableArray<T>(arr: T[]): T[] {
  const [stable, setStable] = useState(arr);
  if (stable.length === arr.length && stable.every((item, i) => item === arr[i])) {
    return stable;
  }
  setStable(arr);
  return arr;
}

function mergeGraphs(a: ForceGraphData, b: ForceGraphData): ForceGraphData {
  const nodeMap = new Map<string, ForceNode>();
  for (const n of a.nodes) nodeMap.set(n.id, n);
  for (const n of b.nodes) if (!nodeMap.has(n.id)) nodeMap.set(n.id, n);

  // After d3 mutates link endpoints from strings to node refs, a naive
  // template-literal key produces "[object Object]|…" and every subsequent
  // merge re-adds the same edge. Resolve both ends back to their string ID
  // first so dedup stays correct across re-renders.
  const linkKey = (l: ForceLink) => `${linkEndId(l.source)}|${linkEndId(l.target)}|${l.predicate}`;
  const linkMap = new Map<string, ForceLink>();
  for (const l of a.links) linkMap.set(linkKey(l), l);
  for (const l of b.links) if (!linkMap.has(linkKey(l))) linkMap.set(linkKey(l), l);

  return { nodes: Array.from(nodeMap.values()), links: Array.from(linkMap.values()) };
}

/**
 * The graph view modes. Tooltips live on the option rather than wrapping each
 * segment, so SegmentedToggle owns the exclusive-selection semantics and this
 * file only supplies the copy.
 */
const VIEW_MODE_OPTIONS: readonly SegmentedOption<ViewMode>[] = [
  {
    value: 'entity',
    label: 'Entity graph',
    tooltip: (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: fontWeights.semibold, display: 'block' }}>
          Entity graph
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          The knowledge graph: Entity → Entity edges from extracted facts
          (e.g. <em>alice — PREFERS → usdc</em>). Node size = degree. Leave the
          owner empty to see the whole-network graph; pick one to scope to
          that user&apos;s knowledge.
        </Typography>
      </Box>
    ),
  },
  {
    value: 'memory',
    label: 'Memory neighbourhood',
    tooltip: (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: fontWeights.semibold, display: 'block' }}>
          Memory neighbourhood
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          Egocentric view around a single memory: walks Memory ↔ Entity
          edges up to a few hops from the seed. Always scoped to one
          owner. Leave the seed empty to auto-pick that user&apos;s
          highest-importance memory.
        </Typography>
      </Box>
    ),
  },
  {
    value: 'full',
    label: 'Full graph',
    tooltip: (
      <Box sx={{ p: 0.5, maxWidth: 320 }}>
        <Typography variant="caption" sx={{ fontWeight: fontWeights.semibold, display: 'block' }}>
          Full graph
        </Typography>
        <Typography variant="caption">
          All memories, entities, and edges for one user. Requires exactly one owner selected.
        </Typography>
      </Box>
    ),
  },
];

export function NimGraphPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('entity');
  const [ownerInput, setOwnerInput] = useState('');
  const [selectedOwners, setSelectedOwners] = useState<OwnerOption[]>([]);
  const [seedMemoryId, setSeedMemoryId] = useState('');
  const [edgeTypeFilter, setEdgeTypeFilter] = useState<string[]>([]);
  const [selectedNode, setSelectedNode] = useState<ForceNode | null>(null);
  const [overlayData, setOverlayData] = useState<ForceGraphData | null>(null);
  const [expandLoading, setExpandLoading] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 800, height: 700 });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const graphRef = useRef<ForceGraphMethods<ForceNode, ForceLink> | undefined>(undefined);

  const trimmedInput = ownerInput.trim();
  const inputIsUuid = isUUID.test(trimmedInput);
  const debouncedSearch = useDebounce(trimmedInput, 300);

  const userSearch = useQuery({
    queryKey: ['nim-graph-owner-search', debouncedSearch],
    queryFn: () => adminClient.searchUsers({ query: debouncedSearch, limit: 8 }),
    enabled: !inputIsUuid && debouncedSearch.length >= 2,
    staleTime: 60 * 1000,
  });

  const searchOptions = useMemo<OwnerOption[]>(() => {
    const profiles = userSearch.data?.profiles ?? [];
    return profiles
      .map((ep) => {
        const p = ep.profile;
        if (!p?.userId) return null;
        const name = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
        return {
          userId: p.userId,
          displayName: name || p.email || p.userId,
          email: p.email ?? '',
        };
      })
      .filter((opt): opt is OwnerOption => opt !== null);
  }, [userSearch.data]);

  const options = useMemo<OwnerOption[]>(() => {
    if (inputIsUuid) {
      return [{ userId: trimmedInput, displayName: 'Use UUID directly', email: trimmedInput }];
    }
    return searchOptions;
  }, [inputIsUuid, trimmedInput, searchOptions]);

  const ownerIds = useMemo(() => selectedOwners.map((o) => o.userId), [selectedOwners]);
  const ownerKey = useMemo(() => [...ownerIds].sort().join(','), [ownerIds]);
  // Memory neighbourhood is anchored at one user — only enable the query when
  // exactly one owner is selected.
  const memoryOwnerId = selectedOwners.length === 1 ? (selectedOwners[0]?.userId ?? '') : '';

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setContainerSize({
        width: Math.max(300, Math.floor(entry.contentRect.width)),
        height: Math.max(400, Math.floor(entry.contentRect.height)),
      });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Stats: filter by the single selected owner if there's one, otherwise show
  // whole-network stats. Multi-owner stats aren't a server endpoint we have,
  // so collapse to "no filter" in that case.
  const stats = useNimGraphStats({
    ownerIdFilter: selectedOwners.length === 1 ? (selectedOwners[0]?.userId ?? undefined) : undefined,
  });

  const memoryQuery = useNimMemoryGraph(
    { ownerId: memoryOwnerId, seedMemoryId: seedMemoryId || undefined },
    viewMode === 'memory' && Boolean(memoryOwnerId),
  );

  // Entity mode: fetch one graph per selected owner and merge. With no owners
  // selected, fall back to a single whole-network query.
  const entityQueries = useQueries({
    queries:
      viewMode !== 'entity'
        ? []
        : ownerIds.length === 0
          ? [
              {
                queryKey: nimGraphKeys.entity({}),
                queryFn: () => adminClient.getNimEntityGraph({}),
              },
            ]
          : ownerIds.map((uid) => ({
              queryKey: nimGraphKeys.entity({ ownerIdFilter: uid }),
              queryFn: () => adminClient.getNimEntityGraph({ ownerIdFilter: uid }),
            })),
  });

  // Full mode: load the complete subgraph for exactly one owner.
  const fullOwnerId = viewMode === 'full' && selectedOwners.length === 1 ? selectedOwners[0]?.userId : undefined;
  const fullQuery = useQuery({
    queryKey: ['nim-graph-full', fullOwnerId],
    queryFn: () => {
      if (!fullOwnerId) {
        throw new Error('The full graph was queried without an owner');
      }
      return adminClient.getNimFullGraph({ ownerId: fullOwnerId });
    },
    enabled: viewMode === 'full' && Boolean(fullOwnerId),
  });

  // useQueries returns a fresh array reference on every render, but each
  // q.data reference is stable while the underlying query data hasn't
  // changed. Dedupe the array shell with a ref so baseData's useMemo only
  // re-runs when the actual data references shift — otherwise an unrelated
  // re-render (e.g. clicking a node to open the drawer) would rebuild
  // baseData → mergedData → graphData with new array references, which makes
  // ForceGraph2D drop accumulated node positions and re-run the simulation.
  const stableEntityDatas = useStableArray(entityQueries.map((q) => q.data));

  const baseData = useMemo<ForceGraphData>(() => {
    if (viewMode === 'memory' && memoryQuery.data) {
      return toForceData(memoryQuery.data.nodes ?? [], memoryQuery.data.edges ?? []);
    }
    if (viewMode === 'entity') {
      let merged: ForceGraphData = { nodes: [], links: [] };
      for (const data of stableEntityDatas) {
        if (data) {
          merged = mergeGraphs(merged, toForceData(data.nodes ?? [], data.edges ?? []));
        }
      }
      return merged;
    }
    if (viewMode === 'full' && fullQuery.data) {
      return toForceData(fullQuery.data.nodes ?? [], fullQuery.data.edges ?? []);
    }
    return { nodes: [], links: [] };
  }, [viewMode, memoryQuery.data, stableEntityDatas, fullQuery.data]);

  const mergedData = useMemo<ForceGraphData>(() => {
    if (!overlayData) return baseData;
    return mergeGraphs(baseData, overlayData);
  }, [baseData, overlayData]);

  // Predicates available in the active dataset, sorted by descending edge
  // count so the most-common edge types appear first in the filter dropdown.
  const edgeTypeOptions = useMemo<string[]>(() => {
    const counts = new Map<string, number>();
    for (const l of mergedData.links) {
      counts.set(l.predicate, (counts.get(l.predicate) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort(([, a], [, b]) => b - a)
      .map(([predicate]) => predicate);
  }, [mergedData]);

  const graphData = useMemo<ForceGraphData>(() => {
    if (edgeTypeFilter.length === 0) return mergedData;
    const allowed = new Set(edgeTypeFilter);
    const filteredLinks = mergedData.links.filter((l) => allowed.has(l.predicate));
    const connectedIds = new Set<string>();
    for (const l of filteredLinks) {
      connectedIds.add(linkEndId(l.source));
      connectedIds.add(linkEndId(l.target));
    }
    const filteredNodes = mergedData.nodes.filter((n) => connectedIds.has(n.id));
    return { nodes: filteredNodes, links: filteredLinks };
  }, [mergedData, edgeTypeFilter]);

  // Collect every UUID-shaped identifier visible in the current graph (node
  // owner IDs and any entity whose rawId is itself a UUID) so we can show
  // emails/names instead of raw UUIDs across the canvas + drawer.
  // NOTE: this uses `rawId` (the entity name / memory UUID), NOT `id` — `id`
  // is the composite client-side key like "ENTITY:<owner>:<name>" which never
  // matches the UUID regex.
  const idsToResolve = useMemo(() => {
    const out: string[] = [];
    for (const n of graphData.nodes) {
      if (n.ownerId) out.push(n.ownerId);
      if (n.kind === GraphNodeKind.GRAPH_NODE_KIND_ENTITY && isUUID.test(n.rawId)) {
        out.push(n.rawId);
      }
    }
    return out;
  }, [graphData]);

  const profilesQuery = useUserProfiles(idsToResolve);
  // See FFlagOverridesPage: `?? {}` is a fresh object each render.
  const profiles = useMemo(() => profilesQuery.data ?? {}, [profilesQuery.data]);

  // Free-text entity rawIds shaped like a person name ("first last", possibly
  // hyphenated). NIM lowercases names in extractions, so the heuristic
  // requires 2-4 lowercase tokens. We look each up via searchUsers and accept
  // only an exact full-name match — this avoids mis-attributing generic
  // multi-word entities ("house deposit", "credit card") to random users.
  const nameCandidates = useMemo(() => {
    const names = new Set<string>();
    for (const n of graphData.nodes) {
      if (n.kind !== GraphNodeKind.GRAPH_NODE_KIND_ENTITY) continue;
      if (isUUID.test(n.rawId)) continue;
      if (n.rawId === 'user') continue;
      if (/^[a-z][a-z'-]*(?:\s[a-z][a-z'-]*){1,3}$/.test(n.rawId)) {
        names.add(n.rawId);
      }
    }
    return Array.from(names).sort();
  }, [graphData]);

  const stableNameCandidates = useStableArray(nameCandidates);

  // Resolve all name-shaped entities to users in a SINGLE batched request
  // (exact full-name match server-side, names already lowercased by the
  // heuristic above) — replaces a per-name searchUsers fan-out that could
  // exhaust the users-service connection pool on large graphs (LIM-1832).
  const nameResolveQuery = useQuery({
    queryKey: ['nim-graph-name-resolve', stableNameCandidates],
    queryFn: async () => {
      const byName = await adminClient.adminBatchResolveUsersByName(stableNameCandidates);
      const out: Record<string, UserProfileMini> = {};
      for (const name of stableNameCandidates) {
        const p = byName[name];
        if (p) out[name] = toMiniProfile(p, p.userId ?? name);
      }
      return out;
    },
    enabled: stableNameCandidates.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  const profilesByName = useMemo(() => nameResolveQuery.data ?? {}, [nameResolveQuery.data]);

  // userProfileForNode resolves an entity node to a known user, if any.
  // Three shapes count as "this node IS a user":
  //   1. rawId is a UUID we resolved to a profile (another user appearing in
  //      the graph) — `ENTITY:<owner>:<other-user-uuid>`.
  //   2. rawId is the literal string "user", NIM's canonical token for the
  //      graph's owner (see product/nim/memory/memory_service/extraction/extractor.go) —
  //      `ENTITY:<owner>:user`. Resolve to the owner's profile.
  //   3. rawId is a free-text name ("luke stanislawski") that searchUsers
  //      mapped to a profile by exact full-name match.
  const userProfileForNode = useCallback(
    (n: ForceNode): UserProfileMini | null => {
      if (n.kind !== GraphNodeKind.GRAPH_NODE_KIND_ENTITY) return null;
      if (isUUID.test(n.rawId)) return profiles[n.rawId] ?? null;
      if (n.rawId === 'user' && n.ownerId) return profiles[n.ownerId] ?? null;
      return profilesByName[n.rawId] ?? null;
    },
    [profiles, profilesByName],
  );

  const renderNodeLabel = useCallback(
    (n: ForceNode): string => {
      const kindLabel = n.kind === GraphNodeKind.GRAPH_NODE_KIND_MEMORY ? 'memory' : 'entity';
      const userProfile = userProfileForNode(n);
      let label: string;
      if (userProfile) {
        label = userProfile.displayName || userProfile.email || n.rawId;
      } else if (n.kind === GraphNodeKind.GRAPH_NODE_KIND_ENTITY && isUUID.test(n.rawId)) {
        // UUID-shaped entity we couldn't resolve — show a short prefix so the
        // canvas doesn't render a wall of 36-character UUIDs.
        label = `unknown (${n.rawId.slice(0, 8)}…)`;
      } else {
        label = n.label || n.rawId;
      }
      return `${label} (${kindLabel})`;
    },
    [userProfileForNode],
  );

  // A different graph has neither the overlay nor the selection of the last.
  const graphKey = `${viewMode}|${ownerKey}|${seedMemoryId}`;
  const [shownGraphKey, setShownGraphKey] = useState(graphKey);
  if (shownGraphKey !== graphKey) {
    setShownGraphKey(graphKey);
    setOverlayData(null);
    setSelectedNode(null);
  }

  // Tune the d3-force simulation so connected nodes spread enough to be
  // readable, but disconnected components don't drift to opposite corners.
  // `charge.distanceMax` is a hard repulsion cutoff: nodes farther apart than
  // this stop pushing each other entirely. Within-cluster spacing is still
  // governed by the full charge strength + link distance, so set distanceMax
  // generously enough to preserve intra-cluster repulsion but tight enough
  // that distant components don't keep drifting apart.
  useEffect(() => {
    const fg = graphRef.current;
    if (!fg || graphData.nodes.length === 0) return;
    const charge = fg.d3Force('charge');
    if (charge && 'strength' in charge && typeof charge.strength === 'function') {
      charge.strength(-380);
    }
    if (charge && 'distanceMax' in charge && typeof charge.distanceMax === 'function') {
      charge.distanceMax(320);
    }
    const link = fg.d3Force('link');
    if (link && 'distance' in link && typeof link.distance === 'function') {
      link.distance(110);
    }
    fg.d3ReheatSimulation();
  }, [graphData]);

  const handleExpand = useCallback(async () => {
    if (!selectedNode) return;
    setExpandLoading(true);
    try {
      const result = await adminClient.expandNimNode({
        // The server populates owner_id on every entity/memory vertex it
        // returns (product/nim/memory/memory_service/graph/graph.go nodeFromVertex), so this should
        // always be set. Don't fall back to a UI-side "currently selected
        // owner" — in multi-select that would silently misroute the expand
        // request to the wrong owner's neighbourhood instead of failing.
        ownerId: selectedNode.ownerId || undefined,
        kind: selectedNode.kind,
        // rawId is the entity name (or memory UUID) — what the server's
        // ExpandNode RPC expects. Stripping the composite Key prefix here was
        // incorrect after the Entity key changed to "ENTITY:<owner>:<name>".
        nodeId: selectedNode.rawId,
      });
      const nextOverlay = toForceData(result.nodes ?? [], result.edges ?? []);
      setOverlayData((prev) => (prev ? mergeGraphs(prev, nextOverlay) : nextOverlay));
    } finally {
      setExpandLoading(false);
    }
  }, [selectedNode]);

  const handleSetAsSeed = useCallback(() => {
    if (!selectedNode || selectedNode.kind !== GraphNodeKind.GRAPH_NODE_KIND_MEMORY) return;
    const memId = selectedNode.id.replace(/^MEMORY:/, '');
    setSeedMemoryId(memId);
    setViewMode('memory');
    setOverlayData(null);
  }, [selectedNode]);

  const isLoading =
    (viewMode === 'memory' && memoryQuery.isLoading) ||
    (viewMode === 'entity' && entityQueries.some((q) => q.isLoading)) ||
    (viewMode === 'full' && fullQuery.isLoading);

  const queryError =
    (viewMode === 'memory' && memoryQuery.error) ||
    (viewMode === 'entity' && entityQueries.find((q) => q.error)?.error) ||
    (viewMode === 'full' && fullQuery.error) ||
    null;

  const memoryModeNeedsOwner = viewMode === 'memory' && selectedOwners.length !== 1;
  const fullModeNeedsOwner = viewMode === 'full' && selectedOwners.length !== 1;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        <SegmentedToggle
          value={viewMode}
          onChange={setViewMode}
          size="sm"
          fullWidth={false}
          aria-label="Graph view mode"
          options={VIEW_MODE_OPTIONS}
        />

        <Tooltip
          title={
            <Box sx={{ p: 0.5, maxWidth: 320 }}>
              <Typography variant="caption" sx={{ fontWeight: fontWeights.semibold, display: 'block' }}>
                Entity graph
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                NIM&apos;s knowledge graph. Nodes are extracted entities; edges
                are predicates between them. Empty owner = whole-network view.
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: fontWeights.semibold, display: 'block', mt: 1 }}>
                Memory neighbourhood
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                Per-user view anchored at one memory. Nodes are Memory and
                Entity; edges follow MENTIONS + fact links from the seed
                outward.
              </Typography>
            </Box>
          }
          placement="bottom-start"
          arrow
        >
          <span>
            <IconButton icon="information-circle" label="View-mode help" size="sm" tone="muted" />
          </span>
        </Tooltip>

        <Autocomplete<OwnerOption, true, false, false>
          multiple
          disableCloseOnSelect
          size="small"
          sx={{ minWidth: 380, flex: 1 }}
          options={options}
          loading={userSearch.isFetching && !inputIsUuid}
          value={selectedOwners}
          inputValue={ownerInput}
          onInputChange={(_, v) => setOwnerInput(v)}
          onChange={(_, opts) => {
            setSelectedOwners(opts);
            setSeedMemoryId('');
          }}
          getOptionLabel={(opt) => opt.displayName}
          isOptionEqualToValue={(a, b) => a.userId === b.userId}
          filterOptions={(x) => x}
          noOptionsText={
            debouncedSearch.length < 2 && !inputIsUuid
              ? 'Type at least 2 characters'
              : 'No users match'
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                selectedOwners.length === 0
                  ? viewMode === 'memory'
                    ? 'Owners — name, email, or UUID (one required)'
                    : 'Owners — name, email, or UUID (optional)'
                  : ''
              }
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <Icon name="search" size={18} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {userSearch.isFetching && !inputIsUuid ? (
                        <LoadingSpinner variant="inline" />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          renderOption={(props, opt) => (
            <li {...props} key={opt.userId}>
              <Stack>
                <Typography variant="body2">{opt.displayName}</Typography>
                {opt.email && opt.email !== opt.displayName ? (
                  <Typography variant="caption" color="text.secondary">
                    {opt.email}
                  </Typography>
                ) : null}
              </Stack>
            </li>
          )}
        />

        {viewMode === 'memory' && (
          <Box sx={{ width: 320 }}>
            <Input
              aria-label="Seed memory ID"
              size="sm"
              placeholder="Seed memory ID (optional)"
              value={seedMemoryId}
              onChange={setSeedMemoryId}
            />
          </Box>
        )}

        <FormControl size="small" sx={{ minWidth: 240, maxWidth: 360 }}>
          <InputLabel id="edge-type-filter-label" shrink>Edge types</InputLabel>
          <Select
            multiple
            notched
            labelId="edge-type-filter-label"
            label="Edge types"
            value={edgeTypeFilter}
            onChange={(e) => {
              const v = e.target.value;
              setEdgeTypeFilter(typeof v === 'string' ? v.split(',') : v);
            }}
            renderValue={(selected) =>
              (selected as string[]).length === 0
                ? 'All edge types'
                : (selected as string[]).join(', ')
            }
            displayEmpty
          >
            {edgeTypeOptions.length === 0 ? (
              <MenuItem disabled value="">
                <em>No edges in current graph</em>
              </MenuItem>
            ) : (
              edgeTypeOptions.map((predicate) => (
                <MenuItem key={predicate} value={predicate}>
                  {predicate}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Stack>

      <StatsRow>
        {stats.isLoading ? (
          <Skeleton width={400} height={32} />
        ) : stats.data ? (
          <>
            <StatusPill label={`${(stats.data.memoryCount ?? 0).toLocaleString()} memories`} tone="neutral" size="sm" />
            <StatusPill label={`${(stats.data.entityCount ?? 0).toLocaleString()} entities`} tone="neutral" size="sm" />
            <StatusPill label={`${(stats.data.totalEdgeCount ?? 0).toLocaleString()} edges`} tone="neutral" size="sm" />
            {(stats.data.edgesByType ?? []).slice(0, 5).map((e) => (
              <StatusPill
                key={e.predicate}
                label={`${e.predicate}: ${(e.count ?? 0).toLocaleString()}`}
                tone="info"
                size="sm"
              />
            ))}
          </>
        ) : null}
      </StatsRow>

      {memoryModeNeedsOwner && (
        <WarningCard tone="info">
          {selectedOwners.length === 0
            ? "Select an owner to render that user's memory neighbourhood."
            : "Memory neighbourhood is anchored at one user — select exactly one owner to render it."}
        </WarningCard>
      )}

      {fullModeNeedsOwner && (
        <WarningCard tone="info">
          {selectedOwners.length === 0
            ? "Select an owner to render their full graph."
            : "Full graph shows one user — select exactly one owner."}
        </WarningCard>
      )}

      {queryError ? <WarningCard tone="error">{getErrorMessage(queryError)}</WarningCard> : null}

      <Card variant="elevated" padding="none">
        <Box ref={containerRef} sx={{ position: 'relative', height: 700 }}>
        {isLoading ? (
          <Box sx={{ p: 4 }}>
            <Skeleton height={600} />
          </Box>
        ) : graphData.nodes.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="text.disabled">
              {memoryModeNeedsOwner || fullModeNeedsOwner ? 'Waiting for owner ID…' : 'No graph data to display.'}
            </Typography>
          </Box>
        ) : (
          <ForceGraph2D
            ref={graphRef}
            width={containerSize.width}
            height={containerSize.height}
            graphData={graphData}
            nodeId="id"
            nodeRelSize={NODE_REL_SIZE}
            nodeLabel={renderNodeLabel}
            nodeColor={(n: ForceNode) => nodeColor(n.kind)}
            nodeVal={nodeVal}
            // Draw user initials inside user-entity circles. 'after' keeps the
            // default circle render and overlays the text on top of it.
            nodeCanvasObjectMode={() => 'after'}
            nodeCanvasObject={(n: ForceNode, ctx: CanvasRenderingContext2D) => {
              const p = userProfileForNode(n);
              if (!p) return;
              const initials = userInitials(p);
              if (!initials) return;
              const r = nodeRadius(n);
              // Two-char initials need ~1.2× their fontSize horizontally;
              // single chars are narrower. Shrink slightly so we never spill
              // outside the circle.
              const fontSize = r * (initials.length > 1 ? 0.9 : 1.1);
              ctx.font = `bold ${fontSize}px sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillStyle = brandColors.white;
              ctx.fillText(initials, n.x ?? 0, n.y ?? 0);
            }}
            linkLabel={(l: ForceLink) => l.predicate}
            linkColor={() => 'rgba(120, 120, 120, 0.4)'}
            linkDirectionalArrowLength={3}
            linkDirectionalArrowRelPos={1}
            // Damping knobs to take the jiggle out of the simulation:
            // - d3VelocityDecay raises the friction so nodes shed momentum faster.
            // - d3AlphaDecay cools the simulation toward "stopped" sooner.
            // - cooldownTicks bounds the absolute number of frames; the
            //   simulation halts once alpha drops below alphaMin or the tick
            //   count is reached, whichever comes first.
            d3VelocityDecay={0.8}
            d3AlphaDecay={0.08}
            cooldownTicks={100}
            onNodeClick={(n) => setSelectedNode(n as ForceNode)}
            onNodeDragEnd={(n) => {
              // Pin the node where the user dropped it. Setting fx/fy makes
              // d3-force treat the position as fixed; the simulation can still
              // move other nodes around it.
              const node = n as ForceNode;
              if (node.x !== undefined) node.fx = node.x;
              if (node.y !== undefined) node.fy = node.y;
            }}
          />
        )}
        </Box>
      </Card>

      <Drawer
        anchor="right"
        open={Boolean(selectedNode)}
        onClose={() => setSelectedNode(null)}
        slotProps={{ paper: { sx: { width: 380 } } }}
      >
        {selectedNode && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Node</Typography>
              <IconButton icon="close" label="Close" size="sm" tone="muted" onClick={() => setSelectedNode(null)} />
            </Stack>

            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Kind</Typography>
                <Typography variant="body2">
                  {selectedNode.kind === GraphNodeKind.GRAPH_NODE_KIND_MEMORY ? 'Memory' : 'Entity'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">Label</Typography>
                {(() => {
                  const userProfile = userProfileForNode(selectedNode);
                  if (userProfile) {
                    return (
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{userProfile.displayName}</Typography>
                        {userProfile.email && (
                          <Typography variant="caption" color="text.secondary">
                            {userProfile.email}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ fontFamily: FONT_STACK_BODY, fontSize: fontSizes.xs, wordBreak: 'break-all' }}>
                          {selectedNode.rawId}
                        </Typography>
                      </Stack>
                    );
                  }
                  if (selectedNode.kind === GraphNodeKind.GRAPH_NODE_KIND_ENTITY && isUUID.test(selectedNode.rawId)) {
                    return (
                      <Stack spacing={0.25}>
                        <Typography variant="body2" color="text.secondary">
                          unknown (no matching user)
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: FONT_STACK_BODY, fontSize: fontSizes.xs, wordBreak: 'break-all' }}>
                          {selectedNode.rawId}
                        </Typography>
                      </Stack>
                    );
                  }
                  return (
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {selectedNode.label}
                    </Typography>
                  );
                })()}
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">Key</Typography>
                <Typography variant="body2" sx={{ fontFamily: FONT_STACK_BODY, fontSize: fontSizes.xs, wordBreak: 'break-all' }}>
                  {selectedNode.id}
                </Typography>
              </Box>

              {selectedNode.ownerId && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Owner</Typography>
                  {profiles[selectedNode.ownerId] ? (
                    <Stack spacing={0.25}>
                      <Typography variant="body2">{profiles[selectedNode.ownerId].displayName}</Typography>
                      {profiles[selectedNode.ownerId].email && (
                        <Typography variant="caption" color="text.secondary">
                          {profiles[selectedNode.ownerId].email}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ fontFamily: FONT_STACK_BODY, fontSize: fontSizes.xs, wordBreak: 'break-all' }}>
                        {selectedNode.ownerId}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ fontFamily: FONT_STACK_BODY, fontSize: fontSizes.xs, wordBreak: 'break-all' }}>
                      {selectedNode.ownerId}
                    </Typography>
                  )}
                </Box>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary">Weight</Typography>
                <Typography variant="body2">{selectedNode.weight.toFixed(3)}</Typography>
              </Box>

              <Stack spacing={1}>
                <Button variant="primary" onClick={handleExpand} loading={expandLoading}>
                  Expand neighbours
                </Button>
                {selectedNode.kind === GraphNodeKind.GRAPH_NODE_KIND_MEMORY && (
                  <Button variant="secondary" onClick={handleSetAsSeed}>
                    Use as memory seed
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        )}
      </Drawer>
    </Stack>
  );
}
